import { getArrivals, postNewGuest } from 'actions/arrivals'
import AddGuestButton from 'components/ReusableComponents/Arrivals/AddGuestButton'
import AddGuestWithPhone from 'components/ReusableComponents/Arrivals/AddGuestWithPhone/index'
import AddGuestWithRoomNo from 'components/ReusableComponents/Arrivals/AddGuestWithRoomNo/index'
import ArrivalList from 'components/ReusableComponents/Arrivals/ArrivalList/index'
import MatchingGuestDialog from 'components/ReusableComponents/Arrivals/MatchinGuestDialog'
import MessagingSetupDialog from 'components/ReusableComponents/Arrivals/MessagingSetupDialog/index'
import CustomHeader from 'components/ReusableComponents/HeaderTitles/index'
import LoadingIndicator from 'components/ReusableComponents/LoadingIndicator'
import usePlausible from 'hooks/usePlausible'
import { useCallback, useEffect, useMemo, useState } from 'react'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useToasts } from 'react-toast-notifications'

const Arrivals = ({ noList }) => {
  const [showSetupDialog, setShowSetupDialog] = useState(false)
  const [showAddGuestWithPhone, setShowAddGuestWithPhone] = useState(false)
  const [showAddGuestWithRoom, setShowAddGuestWithRoom] = useState(false)
  const [showFindMatchDialog, setShowFindMatchDialog] = useState(false)
  const [activeGuest, setActiveGuest] = useState({})
  const [matchingGuest, setMatchingGuest] = useState({})
  const [newGuestRoom, setNewGuestRoom] = useState()

  const dispatch = useDispatch()

  const { arrivals, loading, error } = useSelector(s => s.arrivals)

  const { arrivingGuestsCount, inHouseGuestsCount } = useMemo(() => {
    if (arrivals && arrivals.length > 0) {
      const inHouseCount = arrivals.filter(a => a.status === 'InUse').length
      return {
        inHouseGuestsCount: inHouseCount,
        arrivingGuestsCount: arrivals.length - inHouseCount
      }
    }
    return {
      inHouseGuestsCount: 0,
      arrivingGuestsCount: 0
    }
  }, [arrivals])

  const lastPostedGuest = useSelector(s => s.arrivals.lastPostedGuest)

  const { currentHotel } = useSelector(s => s.login)

  const { addToast } = useToasts()

  useEffect(() => {
    if (lastPostedGuest) {
      addToast(
        `Chat with ${lastPostedGuest.guestFirstName} ${lastPostedGuest.guestLastName} started successfully!`,
        {
          appearance: 'success',
          autoDismiss: true
        }
      )
      setActiveGuest('')
    }
  }, [addToast, lastPostedGuest])

  useEffect(() => {
    if (error) {
      addToast(`${error}`, {
        appearance: 'error',
        autoDismiss: true
      })
      setActiveGuest('')
    }
  }, [addToast, error])

  const { postEvent, plausibleEvents } = usePlausible()

  const handleSetupClick = useCallback(
    id => {
      postEvent(plausibleEvents.StartChatFromArrivals, { id })

      const foundGuest = arrivals.find(a => a.subscriberHotelBookingId === id)
      const {
        guestFirstName,
        guestLastName,
        phoneNumber,
        roomNumber,
        subscriberHotelBookingId
      } = foundGuest

      if (guestFirstName && guestLastName && phoneNumber) {
        dispatch(
          postNewGuest({
            guestFirstName,
            guestLastName,
            guestPhoneNumber: phoneNumber,
            guestPhoneCountryCode: '1',
            roomNumber,
            bookingId: subscriberHotelBookingId
          })
        )
      } else {
        setActiveGuest(foundGuest)
        setShowSetupDialog(true)
      }
    },
    [arrivals, dispatch, plausibleEvents, postEvent]
  )

  const handleNewGuestSubmit = useCallback(
    newGuestRoom => {
      setNewGuestRoom(newGuestRoom)
      const match = arrivals.find(a => a.roomNumber === newGuestRoom)

      setShowAddGuestWithRoom(false)

      if (match) {
        setMatchingGuest(match)
        setShowFindMatchDialog(true)
      } else {
        setActiveGuest({ roomNumber: newGuestRoom })
        setShowSetupDialog(true)
      }
    },
    [arrivals]
  )

  const handleAddGuestClick = useCallback(() => {
    postEvent(plausibleEvents.StartChatFromArrivals)
    setShowAddGuestWithRoom(true)
  }, [plausibleEvents, postEvent])

  const handleSetupGuest = useCallback(
    guestInfo => {
      setShowSetupDialog(false)
      const { guestFirstName, guestLastName, guestPhoneNumber, roomNumber } = guestInfo

      dispatch(
        postNewGuest({
          guestFirstName,
          guestLastName,
          guestPhoneNumber,
          guestPhoneCountryCode: '1',
          roomNumber
        })
      )
    },
    [dispatch]
  )

  const confirmMatchingGuest = useCallback(() => {
    setActiveGuest(matchingGuest)
    setMatchingGuest(null)
    setShowSetupDialog(true)
    setShowFindMatchDialog(false)
  }, [matchingGuest])

  const rejectMatchingGuest = useCallback(() => {
    setActiveGuest({ roomNumber: newGuestRoom })
    setMatchingGuest(null)
    setShowSetupDialog(true)
    setShowFindMatchDialog(false)
  }, [newGuestRoom])

  useEffect(() => {
    dispatch(getArrivals())
  }, [currentHotel, dispatch])

  return (
    <>
      {noList ? (
        <>{loading ? null : <AddGuestButton mini onClick={handleAddGuestClick} />}</>
      ) : (
        <>
          <CustomHeader title="Arrivals &amp; In-house" width={1080} isFixed />
          {loading && <LoadingIndicator />}
          {!loading && (
            <ArrivalList
              arrivals={arrivals}
              handleAddGuestClick={handleAddGuestClick}
              handleSetupClick={handleSetupClick}
              inHouse={inHouseGuestsCount}
              todaysArrivals={arrivingGuestsCount}
            />
          )}
        </>
      )}
      {showSetupDialog && (
        <MessagingSetupDialog
          handleConfirm={handleSetupGuest}
          handleCancel={() => setShowSetupDialog(false)}
          {...activeGuest}
        />
      )}
      {showAddGuestWithPhone && (
        <AddGuestWithPhone
          handleClose={() => setShowAddGuestWithPhone(false)}
          handleSubmit={handleNewGuestSubmit}
        />
      )}
      {showAddGuestWithRoom && (
        <AddGuestWithRoomNo
          handleClose={() => setShowAddGuestWithRoom(false)}
          handleSubmit={handleNewGuestSubmit}
        />
      )}
      {showFindMatchDialog && matchingGuest && (
        <MatchingGuestDialog
          handleSubmit={confirmMatchingGuest}
          handleCancel={rejectMatchingGuest}
          {...matchingGuest}
        />
      )}
    </>
  )
}

export default React.memo(Arrivals)
